/**
 * Datadog Browser telemetry integration (design §3.3, §6.6; RUM per D3).
 *
 * Framework-agnostic wrapper around `@datadog/browser-logs` **and**
 * `@datadog/browser-rum`. It is the single place the rest of the app talks
 * to Datadog: `apiFetch` reports API errors here, the `error.tsx`
 * boundaries report render errors here, and the auth guard sets the user
 * here. Both browser SDKs share this one module and one `initDatadog()`
 * entry point (called from `<DatadogInit />`).
 *
 * Every function is **safe to call before (or without) initialization**.
 * `initDatadog()` no-ops the logs SDK unless `NEXT_PUBLIC_DD_CLIENT_TOKEN`
 * is set, and additionally no-ops RUM unless
 * `NEXT_PUBLIC_DD_RUM_APPLICATION_ID` is *also* set — so the customer pay
 * page (token-less in some environments) and local dev never stand up the
 * SDKs, and RUM stays dark until a Datadog RUM application is provisioned.
 * The report/setUser helpers no-op when their SDK is uninitialized and
 * never throw: observability must not be able to break the page it observes.
 *
 * Browser Logs + RUM ship directly to Datadog's intake over HTTPS using a
 * write-only client token (safe to inline in the bundle, same trust level
 * as the publishable Stripe key) — they do not transit the backend (§1,
 * §6.6). RUM Session Replay is deliberately **disabled**
 * (`sessionReplaySampleRate: 0`): we collect performance / error /
 * resource / user-journey metrics but never record the DOM, which keeps the
 * customer pay page's PII out of Datadog (same posture as the query-string
 * stripping and integer-only `usr.id` below).
 */

import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum } from "@datadog/browser-rum"

/** Default Datadog site (US3 region) per design D4. */
const DEFAULT_DD_SITE = "us3.datadoghq.com"
/** Default service tag for browser telemetry per design §6.8. */
const DEFAULT_DD_SERVICE = "billdozer-frontend-browser"
/** Default RUM session sample rate (%) when the env override is absent. */
const DEFAULT_RUM_SAMPLE_RATE = 100

/**
 * Per-SDK init guards. Each SDK's own `init()` warns and no-ops on a second
 * call, but we track them ourselves so the report/setUser helpers can
 * cheaply short-circuit before touching the SDK at all. They are tracked
 * separately because RUM has a stricter gate (it also needs an application
 * id), so logs can be live while RUM is not.
 */
let logsInitialized = false
let rumInitialized = false

/** Context captured at the `apiFetch` throw site for an API error. */
export interface ApiErrorContext {
  method: string
  /** Request path; the query string is stripped before logging (§6.7). */
  path: string
  status: number
  /** Parsed error body (Ninja `{detail: ...}` shape, or undefined). */
  detail?: unknown
}

/** Strip the query string from a URL/path — it can carry signed tokens (§6.7). */
function stripQuery(path: string): string {
  const queryIndex = path.indexOf("?")
  return queryIndex === -1 ? path : path.slice(0, queryIndex)
}

/** Normalize an unknown thrown value into Datadog's `error.*` trio. */
function describeError(err: unknown): {
  kind: string
  message: string
  stack?: string
} {
  if (err instanceof Error) {
    return { kind: err.name, message: err.message, stack: err.stack }
  }
  return { kind: "Error", message: String(err) }
}

/**
 * Resolve the RUM session sample rate from `NEXT_PUBLIC_DD_RUM_SAMPLE_RATE`,
 * clamped to 0–100. Falls back to {@link DEFAULT_RUM_SAMPLE_RATE} when the
 * env var is absent or not a finite number.
 */
function resolveRumSampleRate(): number {
  const raw = process.env.NEXT_PUBLIC_DD_RUM_SAMPLE_RATE
  if (raw === undefined || raw === "") return DEFAULT_RUM_SAMPLE_RATE
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return DEFAULT_RUM_SAMPLE_RATE
  return Math.min(100, Math.max(0, parsed))
}

/**
 * Same-origin tracing allowlist for RUM → backend APM correlation. Injecting
 * W3C `traceparent` (+ Datadog) headers onto same-origin `/api` requests lets
 * a frontend RUM action link to the backend `ddtrace` span it triggered.
 * Same-origin only, so there is no CORS preflight (the browser hits the
 * Next.js `/api` proxy on its own origin). Empty when there is no `window`
 * (SSR / non-DOM test), which disables injection harmlessly.
 */
function rumTracingUrls(): Array<{
  match: string
  propagatorTypes: Array<"tracecontext" | "datadog">
}> {
  if (typeof window === "undefined") return []
  return [
    { match: window.location.origin, propagatorTypes: ["tracecontext", "datadog"] },
  ]
}

/**
 * Initialize the Browser Logs + RUM SDKs once each. No-op unless a client
 * token is configured; RUM additionally requires an application id.
 * Idempotent per SDK. Reads its unified-tagging values from `NEXT_PUBLIC_DD_*`
 * build-time env (inlined at build, like `NEXT_PUBLIC_HUBSPOT_PORTAL_ID`).
 */
export function initDatadog(): void {
  const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
  if (!clientToken) return

  const site = process.env.NEXT_PUBLIC_DD_SITE || DEFAULT_DD_SITE
  const service = process.env.NEXT_PUBLIC_DD_SERVICE || DEFAULT_DD_SERVICE
  const env = process.env.NEXT_PUBLIC_DD_ENV
  const version = process.env.NEXT_PUBLIC_DD_VERSION

  if (!logsInitialized) {
    datadogLogs.init({
      clientToken,
      site,
      service,
      env,
      version,
      // Auto-capture uncaught exceptions + console errors as a safety net
      // beyond the explicit reportApiError/reportRenderError calls (§6.6).
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    })
    logsInitialized = true
  }

  const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID
  if (applicationId && !rumInitialized) {
    datadogRum.init({
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
      sessionSampleRate: resolveRumSampleRate(),
      // Session Replay off: metrics/perf/errors/resources only, no DOM
      // recording (see the module doc comment).
      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      // Irrelevant while replay is off, but documents intent and future-proofs
      // a later replay enablement to mask inputs by default.
      defaultPrivacyLevel: "mask-user-input",
      // RUM → backend APM correlation on same-origin /api calls (design D3).
      allowedTracingUrls: rumTracingUrls(),
    })
    rumInitialized = true
  }
}

/**
 * Log a failed API call as a structured `api_error` (design §4.3 shape).
 * No-op when the Logs SDK isn't initialized; never throws. (RUM captures the
 * failed request independently via resource tracking, so this stays
 * logs-only to avoid double-counting.)
 */
export function reportApiError(ctx: ApiErrorContext): void {
  if (!logsInitialized) return

  try {
    const message =
      ctx.detail === undefined
        ? "api_error"
        : `api_error ${JSON.stringify(ctx.detail)}`
    datadogLogs.logger.error(message, {
      http: {
        method: ctx.method,
        url_details: { path: stripQuery(ctx.path) },
        status_code: ctx.status,
      },
      error: {
        kind: "ApiError",
        message: `${ctx.status} ${stripQuery(ctx.path)}`,
      },
    })
  } catch {
    // Observability must never break the caller.
  }
}

/**
 * Log a render-boundary failure as a structured `render_error` with the
 * Datadog `error.kind/message/stack` trio, and forward it to RUM as an
 * explicit error. RUM does not auto-capture these because the React error
 * boundary swallows the exception before it reaches `window.onerror`. No-op
 * per SDK when uninitialized; never throws.
 */
export function reportRenderError(err: unknown): void {
  if (logsInitialized) {
    try {
      datadogLogs.logger.error("render_error", { error: describeError(err) })
    } catch {
      // Observability must never break the caller.
    }
  }

  if (rumInitialized) {
    try {
      datadogRum.addError(err, { source: "render_boundary" })
    } catch {
      // Observability must never break the caller.
    }
  }
}

/**
 * Stamp the signed-in user's id onto subsequent browser logs + RUM sessions
 * (`usr.id`). Integer PK only — never the email (PII, §6.7). No-op per SDK
 * when uninitialized; never throws.
 */
export function setDatadogUser(id: number | string): void {
  if (logsInitialized) {
    try {
      datadogLogs.setUser({ id: String(id) })
    } catch {
      // Best-effort; never break auth resolution.
    }
  }

  if (rumInitialized) {
    try {
      datadogRum.setUser({ id: String(id) })
    } catch {
      // Best-effort; never break auth resolution.
    }
  }
}

/**
 * Test-only seam. The unit suite runs in one shared process under
 * `--experimental-test-isolation=none`, so the module-level init flags would
 * otherwise leak across files. Tests call this to restore the pristine
 * (uninitialized) state. Not used in production code.
 */
export function __resetDatadogForTests(): void {
  logsInitialized = false
  rumInitialized = false
}
